import { selectEntityDisplayName } from '@opengov/app-core/entity';
import { composeEffects } from '@opengov/compose-app/core/module';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';
import { IOgqlV1QueryResult } from '@opengov/ogql-types';
import { removeNoCodes } from '@opengov/reporting-transforms/visualizations';
import {
  EQueryType,
  IReportConfiguration,
  IReportDatasetLens,
  ITemplateReadable,
  OG_CHILD_COUNT_COLUMN_NAME
} from '@opengov/reporting-types';
import { produce } from 'immer';
import Papa from 'papaparse';
import sanitize from 'sanitize-filename';

import { selectReportDataTableColumnDefs as selectAgGridColumnDefs } from '../../reportExplorer/ReportDataTableContainer/selectors/selectReportDataTableColumnDefs';
import {
  selectDataTableTotalRowCount,
  selectReportConfigWithMetadata
} from '../../reportExplorer/selectors/selectReportExplorerState';
import { IReportLocale, reportLocaleText } from '../locale';
import { selectCurrentReportTitle } from '../report/selectors/reportSelectors';
import { runV0Query } from '../reportConfiguration/effects/queries/runQueries';
import { selectIsInStandardReport } from '../reportConfiguration/selectors';
import { selectCurrentLens, selectCurrentTemplate } from '../reportTemplate/standardTemplateSelectors';
import { CSV_BLOB_TYPE, CSV_UTF8_BOM } from '../helpers/csvEncoding';
import { MuiToastTypes } from '../helpers/utils';
import { reportActionCreators } from '../report/state';

const EXPORT_ROW_LIMIT = 50000;

export type IExecutePublicExport = {
  columnDefs: any;
  reportMsgs: IReportLocale;
  reportConfiguration: IReportConfiguration;
  useQueryService: boolean;
  reportTitle: string;
  entityName: string;
  rowCount: number;
  isStandardTemplate: boolean;
  currentTemplate?: ITemplateReadable;
  lens: IReportDatasetLens;
};

const buildAndDownloadFile = (fileContent, fileName) => {
  const csvWithBom = CSV_UTF8_BOM + fileContent;
  if ((window.navigator as { msSaveBlob?: (...args) => void }).msSaveBlob) {
    (window.navigator as { msSaveBlob?: (...args) => void }).msSaveBlob(
      new Blob([csvWithBom], { type: CSV_BLOB_TYPE }),
      fileName
    );
  } else {
    const element = document.createElement('a');
    const file = new Blob([csvWithBom], { type: CSV_BLOB_TYPE });
    element.href = URL.createObjectURL(file);
    element.download = fileName;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }
};

export enum EExecutePublicExportKind {
  GroupBy = 'GroupBy',
  Record = 'Record'
}

export const executePublicExportFn =
  (reportActions: typeof reportActionCreators, runQuery: typeof runV0Query, makeTheFile = buildAndDownloadFile) =>
  async (state: IExecutePublicExport, e: { kind: EExecutePublicExportKind }): Promise<unknown[]> => {
    const {
      columnDefs,
      reportConfiguration,
      reportMsgs: {
        reportExplorer: { emptyValues },
        reportTable: {
          publicDetailTableExport: {
            exportDurationTruncationWarning,
            exportDurationWarning,
            exportInProgress,
            exportFailed,
            exportFailedMessage
          }
        }
      },
      useQueryService,
      reportTitle,
      entityName,
      rowCount,
      isStandardTemplate,
      currentTemplate,
      lens
    } = state;

    const theReportTitle = isStandardTemplate ? currentTemplate.standardReportName : reportTitle;
    const columns = reportConfiguration.detailTableQuery.columns;
    const groupByColumns = reportConfiguration.detailTableQuery.groupByColumns;
    const groupByColumn = columns.find((c) => c.name === groupByColumns[0]);
    const columnsInDataTableOrder = columnDefs.map((col) => columns.find((c) => c.name === col.colId));

    // the groupByColumn does not show up in the list of columnDefs
    if (groupByColumns?.length) {
      columnsInDataTableOrder.unshift(groupByColumn);
    }

    const reportConfigurationForQuery = produce(reportConfiguration, (draft) => {
      if (e.kind === EExecutePublicExportKind.Record) {
        // it might be a grouped table .. so we have to remove the groupByColumns
        draft.detailTableQuery.groupByColumns = [];
      }

      draft.detailTableQuery.columns = columnsInDataTableOrder;
    });

    let data;
    let queriedColumns: { name: string; label?: string; displayName?: string }[];
    const dataWillBeTruncated = rowCount > EXPORT_ROW_LIMIT;

    reportActions.toggleMuiToast({
      type: MuiToastTypes.SUCCESS,
      headerText: exportInProgress,
      bodyText: dataWillBeTruncated ? exportDurationTruncationWarning : exportDurationWarning
    });

    try {
      const result = await runQuery({
        lensId: lens && lens.id,
        reportId: reportConfigurationForQuery.reportId,
        queryType: EQueryType.detailTableExportQuery,
        body: isStandardTemplate ? { reportConfiguration: reportConfigurationForQuery } : reportConfigurationForQuery,
        useLargeScaleQueryLimit: false,
        useQueryService,
        limit: EXPORT_ROW_LIMIT,
        isStandardTemplate
      });
      const { jobResult } = result;
      if (!jobResult) {
        throw new Error();
      }
      data = jobResult.data;

      queriedColumns = (jobResult as unknown as IOgqlV1QueryResult)?.metadata?.columns || jobResult?.columns;
    } catch (e) {
      console.error(e);
      reportActions.toggleMuiToast({
        type: MuiToastTypes.FAILURE,
        headerText: exportFailed,
        bodyText: exportFailedMessage
      });
      return;
    }

    const visibleColumns = queriedColumns.filter(
      (column) => !columnDefs.find((def) => def.colId === column.name)?.hide
    );

    const queriedHeaders = visibleColumns
      .map((c) => c.name)
      .filter((name) => name !== OG_CHILD_COUNT_COLUMN_NAME)
      .map((name) => columnDefs.find((def) => def.colId === name)?.headerName)
      .filter((name) => !!name);

    const shownColumnIndexes = queriedColumns.map((column, index) =>
      visibleColumns.find((colDef) => colDef.name === column.name) ? index : -1
    );

    if (groupByColumns?.length) {
      queriedHeaders.unshift(groupByColumn.displayName);
      shownColumnIndexes.unshift(0);
      // Add Empty Values text to the first cell in the row (the group header cell) if it's empty
      data = data.map((r) => r.map((v, i) => (i == 0 && v === '' ? emptyValues : v)));
    }

    const formattedData = data.map((row) =>
      row.filter((value, index) => shownColumnIndexes.includes(index)).map((value) => removeNoCodes(value, ''))
    );

    const fileContent = Papa.unparse({ fields: queriedHeaders, data: formattedData });
    const fileName =
      e.kind === EExecutePublicExportKind.GroupBy
        ? sanitize(`${entityName}_${theReportTitle}_subtotals_export.csv`)
        : sanitize(`${entityName}_${theReportTitle}_detail_export.csv`);
    makeTheFile(fileContent, fileName);

    return data;
  };

export const detailsTableExportEffects = composeEffects('details-table-export', (compose) => ({
  executePublicExport: compose.statefulEffect(
    createStructuredSelector({
      columnDefs: selectAgGridColumnDefs,
      reportMsgs: reportLocaleText,
      reportConfiguration: selectReportConfigWithMetadata,
      useQueryService: selectUseQueryService,
      reportTitle: selectCurrentReportTitle,
      entityName: selectEntityDisplayName,
      rowCount: selectDataTableTotalRowCount,
      isStandardTemplate: selectIsInStandardReport,
      currentTemplate: selectCurrentTemplate,
      lens: selectCurrentLens
    }),
    executePublicExportFn(reportActionCreators, runV0Query, buildAndDownloadFile)
  )
}));
